import { Box, Button, TextField } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, TrashIcon } from '~/assets/icons';
import { BaseHeaderTitle } from '~/components/common';
import { useTranslation } from 'react-i18next';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { catchError, handleApiError, notify } from '~/utils/helper';

import useDebounce from '~/hooks/useDebounce';

import styles from './CRMStatus.module.scss';
import classNames from 'classnames/bind';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';

import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';

import { IPayloadDeletePriceComponent } from '~/utils/interface/estimate';

import { ICRMStatus } from '~/utils/interface/crm-status';
import { handleGetCRMStatus, handleGetCRMStatuses } from '~/thunks/crm-status/crmStatusThunk';
import { crmStatusActions } from '~/thunks/crm-status/crmStatusSlice';
import crmStatusApi from '~/apis/crm-status';
import EditCRMStatusDialog from '~/components/crm-status/EditCRMStatus/EditCRMStatusDialog';
import CRMStatusList from '~/components/crm-status/CRMStatusList/CRMStatusList';

const cx = classNames.bind(styles);

const CRMStatus = () => {
  const permissions = usePermission();
  const [searchInput, setSearchInput] = useState<string>('');
  const { t } = useTranslation(['admin/crm-status', 'admin/component']);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [editDialogMode, setEditDialogMode] = useState<string>('');

  const dispatch = useAppDispatch();
  const searchInputDebounce = useDebounce<string>(searchInput);
  const [editData, setEditData] = useState<ICRMStatus>();
  const { filter, needRefreshData, selectedIds, openAlertDelete, menuSelectedId } = useAppSelector(
    (state) => state.crmStatus
  );

  const handleSetOpen = async (open: boolean, _needRefreshData?: boolean) => {
    setOpenEditDialog(open);
  };

  const handleOpen = () => {
    setEditDialogMode(DIALOG_MODE_CREATE);
    setOpenEditDialog(true);
  };

  const handleOpenEdit = async (id: string) => {
    const response = await dispatch(handleGetCRMStatus(id)).unwrap();
    setEditData(response);
    setEditDialogMode(DIALOG_MODE_EDIT);
    setOpenEditDialog(true);

    try {
    } catch (e) {
      handleApiError(e);
    }
  };

  const openAlertDeleteModal = useCallback(() => {
    dispatch(crmStatusActions.changeOpenAlertDelete(true));
  }, [dispatch]);

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(crmStatusActions.changeMenuSelectId(undefined));
    dispatch(crmStatusActions.changeOpenAlertDelete(false));
  }, [dispatch]);

  const onDeleteSelectedCRMStatuses = useCallback(async () => {
    let payloadDeleteCRMStatuses: IPayloadDeletePriceComponent =
      menuSelectedId !== undefined ? { ids: [menuSelectedId] } : { ids: [...selectedIds] };
    try {
      await crmStatusApi.delete(payloadDeleteCRMStatuses);
      notify('success', t('deleteSuccessMessage'));
      dispatch(crmStatusActions.changeNeedRefreshData(true));
      dispatch(crmStatusActions.changeOpenAlertDelete(false));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  }, [dispatch, menuSelectedId, selectedIds, t]);

  useEffect(() => {
    if (dispatch && typeof searchInputDebounce !== 'undefined') {
      dispatch(
        crmStatusActions.setFilter({
          search: searchInputDebounce,
          searchFields: ['name'],
          page: 1,
        })
      );
    }
  }, [searchInputDebounce, dispatch]);

  useEffect(() => {
    dispatch(handleGetCRMStatuses({ ...filter, limit: 0 }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch, filter, needRefreshData]);

  return (
    <Box className='w-full h-full'>
      <Box className='mt-[20px] flex flex-col min-[1080px]:flex-row item-center'>
        <Box className='flex flex-col min-[1080px]:flex-row min-[1080px]:flex-1 min-[1080px]:justify-between'>
          <BaseHeaderTitle title={t('title')} />
          <Box className='flex flex-row mt-[10px] min-[1080px]:mt-0 gap-[10px]'>
            {permissions[PERMISSIONS.crmStatusSetting_delete] && (
              <Button
                className={cx(
                  { 'visibility: visible': selectedIds.length > 0 },
                  { 'visibility: hidden': selectedIds.length <= 0 }
                )}
                onClick={openAlertDeleteModal}
                variant='contained'
                color='error'
                disableElevation
                startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='white' />}
              >
                {t('delete')}
              </Button>
            )}
            <TextField
              className='w-[204px]'
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('searchPlaceholder')}
              InputProps={{ startAdornment: <SearchIcon className='w-[16px]' /> }}
            />
          </Box>
        </Box>

        {permissions[PERMISSIONS.projectStatus_create] && (
          <Box className='flex flex-row mt-[10px] min-[1080px]:flex-row min-[1080px]:ml-[10px] min-[1080px]:mt-0 justify-between'>
            <Button
              className='h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-[8px] ml-[10px]'
              variant='contained'
              disableElevation
              startIcon={<PlusIcon className='w-[10px] h-[10px]' />}
              onClick={handleOpen}
            >
              {t('addNew')}
            </Button>
            <EditCRMStatusDialog open={openEditDialog} setOpen={handleSetOpen} mode={editDialogMode} data={editData} />
          </Box>
        )}
      </Box>
      <Box className='mt-[40px]'>
        <CRMStatusList onOpenEdit={handleOpenEdit} data={editData} />
      </Box>
      <ModalAlertDelete
        open={openAlertDelete}
        description={t('alertDescription')}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelectedCRMStatuses}
      />
    </Box>
  );
};

export default CRMStatus;
